import type { DataProvider } from '@refinedev/core'

import { apiClient } from './api-client'

/**
 * 自定义 Data Provider：对接 Fastify 后端
 *
 * 后端 API 非标准 REST，按 resource 名字分别处理。
 * 特殊操作（issue/rebind/revoke 等）通过 custom 方法暴露。
 *
 * 资源映射：
 *   projects            → /api/admin/projects
 *   project-licenses    → /api/admin/:projectCode/licenses  (meta.projectCode 必填)
 *   project-devices     → /api/admin/:projectCode/devices   (meta.projectCode 必填)
 *   project-activations → /api/admin/:projectCode/activations
 *   project-stats       → /api/admin/:projectCode/stats
 *   platform-stats      → /api/admin/stats
 */

/** 从 meta 取 projectCode（项目级资源必填） */
function getProjectCode(meta?: Record<string, unknown>): string {
  const code = meta?.projectCode as string | undefined
  if (!code) {
    throw new Error('meta.projectCode 必填：项目级资源需要 projectCode')
  }
  return code
}

/** 后端列表响应格式：{ items, total?, page?, pageSize? } | 数组 */
interface ListResponse<T> {
  items?: T[]
  total?: number
  page?: number
  pageSize?: number
}

/** 解析后端列表响应，归一化为 Refine 期望的格式 */
function normalizeList<T>(
  raw: ListResponse<T> | T[],
  _pagination?: { current?: number; pageSize?: number }
): { data: T[]; total: number } {
  if (Array.isArray(raw)) {
    return { data: raw, total: raw.length }
  }
  return {
    data: raw.items ?? [],
    total: raw.total ?? raw.items?.length ?? 0
  }
}

/** 构建 query string */
function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.append(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const dataProvider: DataProvider = {
  // ── 列表查询 ─────────────────────────────────────────
  async getList({ resource, pagination, sorters: _sorters, filters, meta }) {
    const query: Record<string, unknown> = {}

    if (pagination) {
      query.page = pagination.current ?? 1
      query.pageSize = pagination.pageSize ?? 20
    }

    // 简化的 filter 处理（后端只支持精确匹配的 query 参数）
    if (filters) {
      for (const f of filters) {
        if (f.operator === 'eq' && f.value !== undefined) {
          query[f.field] = f.value
        }
      }
    }

    // sorters 暂不传递（后端目前按 createdAt 倒序）

    let url: string
    switch (resource) {
      case 'projects':
        url = '/api/admin/projects'
        break
      case 'project-licenses':
        url = `/api/admin/${getProjectCode(meta)}/licenses`
        break
      case 'project-devices':
        url = `/api/admin/${getProjectCode(meta)}/devices`
        break
      case 'project-activations':
        url = `/api/admin/${getProjectCode(meta)}/activations`
        break
      default:
        throw new Error(`getList 不支持的资源: ${resource}`)
    }

    const { data } = await apiClient.get(url + buildQuery(query))
    return normalizeList(data, pagination)
  },

  // ── 单条查询 ─────────────────────────────────────────
  async getOne({ resource, id, meta: _meta }) {
    let url: string
    switch (resource) {
      case 'projects':
        url = `/api/admin/projects/${id}`
        break
      default:
        throw new Error(`getOne 不支持的资源: ${resource}`)
    }
    const { data } = await apiClient.get(url)
    return { data }
  },

  // ── 创建 ─────────────────────────────────────────────
  async create({ resource, variables }) {
    if (resource !== 'projects') {
      throw new Error(`create 仅支持 projects 资源，不支持: ${resource}`)
    }
    const { data } = await apiClient.post('/api/admin/projects', variables)
    return { data }
  },

  // ── 更新 ─────────────────────────────────────────────
  async update({ resource, id, variables }) {
    if (resource !== 'projects') {
      throw new Error(`update 仅支持 projects 资源，不支持: ${resource}`)
    }
    const { data } = await apiClient.patch(`/api/admin/projects/${id}`, variables)
    return { data }
  },

  // ── 删除（后端未提供，留空） ────────────────────────
  async deleteOne() {
    throw new Error('后端未提供删除接口，请使用暂停/归档')
  },

  // ── getApiUrl（Refine 规范要求） ─────────────────────
  getApiUrl() {
    return apiClient.defaults.baseURL ?? ''
  },

  // ── custom：处理 issue/rebind/revoke/rotate-key 等特殊操作 ──
  async custom({ url, method, payload, query, meta }) {
    let finalUrl = url ?? ''

    // 支持 {projectCode} 占位符
    if (meta?.projectCode && finalUrl.includes('{projectCode}')) {
      finalUrl = finalUrl.replace('{projectCode}', String(meta.projectCode))
    }

    if (query) {
      finalUrl += buildQuery(query)
    }

    const httpMethod = (method ?? 'post').toLowerCase() as
      | 'get'
      | 'post'
      | 'patch'
      | 'put'
      | 'delete'

    const config = payload ? { data: payload } : undefined
    const { data } = await apiClient[httpMethod](finalUrl, config?.data)
    return { data }
  }
}
